import { type JSX, useMemo } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button, Chip, Input } from '@heroui/react';

import { useRefArchManifest } from '~/hooks/useReferenceArchitectures';
import { usePatternsStore } from '~/stores/patternsStore';

import type { PatternManifestEntry, PatternSeverity } from '~/types';

import { DOMAIN_ORDER, formatDomainLabel, formatRefArchTitle } from '../utils';

interface PatternFiltersProps {
  patterns: PatternManifestEntry[];
  filteredCount: number;
}

const SEVERITY_OPTIONS: Array<{
  color: 'danger' | 'primary' | 'default';
  label: string;
  value: PatternSeverity;
}> = [
  { value: 'critical', label: 'Critical', color: 'danger' },
  { value: 'recommended', label: 'Recommended', color: 'primary' },
  { value: 'advisory', label: 'Advisory', color: 'default' },
];

export function PatternFilters({ filteredCount, patterns }: PatternFiltersProps): JSX.Element {
  const {
    resetFilters,
    searchQuery,
    selectedDomains,
    selectedRefArchId,
    selectedSeverities,
    setSearchQuery,
    setSelectedRefArchId,
    toggleDomain,
    toggleSeverity,
  } = usePatternsStore();

  const { manifest: refArchManifest } = useRefArchManifest();

  const hasActiveFilters =
    searchQuery ||
    selectedDomains.size > 0 ||
    selectedSeverities.size > 0 ||
    selectedRefArchId !== null;

  // Only show domains that have patterns, ordered by DOMAIN_ORDER then alphabetically
  const availableDomains = useMemo(() => {
    const present = new Set(patterns.map(p => p.domain));
    const ordered = DOMAIN_ORDER.filter(d => present.has(d));
    // Append any domains not in DOMAIN_ORDER
    for (const d of [...present].sort()) {
      if (!DOMAIN_ORDER.includes(d)) ordered.push(d);
    }
    return ordered;
  }, [patterns]);

  return (
    <div className="space-y-3 mb-6">
      <div className="flex items-center gap-3">
        <Input
          placeholder="Search patterns..."
          value={searchQuery}
          onValueChange={setSearchQuery}
          startContent={<MagnifyingGlassIcon className="h-4 w-4 text-muted" />}
          size="sm"
          className="max-w-xs"
          isClearable
          onClear={() => setSearchQuery('')}
        />
        {hasActiveFilters && (
          <Button
            size="sm"
            variant="light"
            onPress={resetFilters}
            startContent={<XMarkIcon className="h-4 w-4" />}
          >
            Clear all
          </Button>
        )}
        <span className="text-xs text-muted ml-auto">
          {filteredCount} of {patterns.length} patterns
        </span>
      </div>

      {/* Architecture chips */}
      {refArchManifest && (
        <div className="flex flex-wrap gap-1.5">
          {refArchManifest.reference_architectures.map(ra => {
            const isSelected = selectedRefArchId === ra.ref_arch_id;
            return (
              <Chip
                key={ra.ref_arch_id}
                size="sm"
                variant={isSelected ? 'solid' : 'bordered'}
                color={isSelected ? 'secondary' : 'default'}
                className="cursor-pointer"
                onClick={() => setSelectedRefArchId(isSelected ? null : ra.ref_arch_id)}
              >
                {formatRefArchTitle(ra.variant)}
              </Chip>
            );
          })}
        </div>
      )}

      {/* Domain chips */}
      <div className="flex flex-wrap gap-1.5">
        {availableDomains.map(domain => (
          <Chip
            key={domain}
            size="sm"
            variant={selectedDomains.has(domain) ? 'solid' : 'bordered'}
            color={selectedDomains.has(domain) ? 'primary' : 'default'}
            className="cursor-pointer"
            onClick={() => toggleDomain(domain)}
          >
            {formatDomainLabel(domain)}
          </Chip>
        ))}
      </div>

      {/* Severity chips */}
      <div className="flex flex-wrap gap-1.5">
        {SEVERITY_OPTIONS.map(({ color, label, value }) => (
          <Chip
            key={value}
            size="sm"
            variant={selectedSeverities.has(value) ? 'solid' : 'bordered'}
            color={selectedSeverities.has(value) ? color : 'default'}
            className="cursor-pointer"
            onClick={() => toggleSeverity(value)}
          >
            {label}
          </Chip>
        ))}
      </div>
    </div>
  );
}
